import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Switch,
  Tab,
  Tabs,
  Textarea,
} from '@nextui-org/react';
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Globe,
  History,
  Newspaper,
  Pencil,
  Play,
  Plus,
  Save,
  Settings,
  Sparkles,
  Trash2,
  Trophy,
  XCircle,
} from 'lucide-react';
import adminNewsAutomationService, {
  AutomationRun,
  AutomationSettings,
  AutomationSource,
  AutomationSourceType,
  PendingArticle,
} from '../../services/adminNewsAutomationService';
import newsService from '../../services/newsService';

const ACCENT = '#3fced1';
const PENDING_PAGE_SIZE = 12;
const RUNS_PAGE_SIZE = 10;

type TabKey = 'review' | 'sources' | 'settings' | 'runs';

// ---------- Helpers ----------

function formatDuration(startedAt?: number | null, finishedAt?: number | null): string {
  if (!startedAt || !finishedAt || finishedAt < startedAt) return '';
  const seconds = Math.round((finishedAt - startedAt) / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
}

function formatDateTime(ms?: number | null): string {
  if (!ms || typeof ms !== 'number') return '';
  try {
    return new Date(ms).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function sourceTypeLabel(type?: string | null): string {
  if (type === 'DARTCONNECT_TOURNAMENT') return 'DartConnect-Turnier';
  if (type === 'MSS_SERIES') return 'MSS-Serie';
  if (type === 'WEBSITE') return 'Website';
  return type || 'Unbekannt';
}

// ---------- Feedback-Banner ----------

const FeedbackBanner: React.FC<{
  message: string | null;
  type: 'success' | 'error';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [message, onClose]);

  if (!message) return null;
  return (
    <div
      className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
        type === 'success'
          ? 'border-green-500/40 bg-green-500/10 text-green-400'
          : 'border-red-500/40 bg-red-500/10 text-red-400'
      }`}
    >
      {type === 'success' ? (
        <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
      ) : (
        <XCircle size={18} className="mt-0.5 shrink-0" />
      )}
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={onClose}
        className="text-current opacity-70 hover:opacity-100"
        aria-label="Meldung schließen"
      >
        ✕
      </button>
    </div>
  );
};

// ---------- Review-Tab ----------

const ArticleBanner: React.FC<{ article: PendingArticle; className?: string }> = ({
  article,
  className = '',
}) => {
  const imageUrl = newsService.getImageUrl(article);
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={newsService.formatTitle(article)}
        className={`w-full aspect-video object-cover ${className}`}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }
  return (
    <div
      className={`w-full aspect-video flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-950 ${className}`}
    >
      <Newspaper size={40} className="text-zinc-600" />
    </div>
  );
};

interface ReviewTabProps {
  onPendingTotalChange: (total: number) => void;
}

const ReviewTab: React.FC<ReviewTabProps> = ({ onPendingTotalChange }) => {
  const [articles, setArticles] = useState<PendingArticle[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<PendingArticle | null>(null);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const updateTotal = useCallback(
    (value: number) => {
      setTotal(value);
      onPendingTotalChange(value);
    },
    [onPendingTotalChange]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminNewsAutomationService.getPendingArticles({
        limit: PENDING_PAGE_SIZE,
        offset: 0,
      });
      setArticles(res.articles);
      updateTotal(res.total);
    } catch (err) {
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Artikel konnten nicht geladen werden.'
        )
      );
    } finally {
      setLoading(false);
    }
  }, [updateTotal]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await adminNewsAutomationService.getPendingArticles({
        limit: PENDING_PAGE_SIZE,
        offset: articles.length,
      });
      setArticles((prev) => {
        const existingIds = new Set(
          prev.map((a) => adminNewsAutomationService.getId(a)).filter(Boolean)
        );
        const fresh = res.articles.filter(
          (a) => !existingIds.has(adminNewsAutomationService.getId(a))
        );
        return [...prev, ...fresh];
      });
      updateTotal(res.total);
    } catch (err) {
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Weitere Artikel konnten nicht geladen werden.'
        )
      );
    } finally {
      setLoadingMore(false);
    }
  };

  const openArticle = (article: PendingArticle) => {
    setSelected(article);
    setConfirmApprove(false);
    setShowReject(false);
    setRejectReason('');
  };

  const closeModal = () => {
    setSelected(null);
    setConfirmApprove(false);
    setShowReject(false);
    setRejectReason('');
  };

  const removeFromList = (article: PendingArticle) => {
    const id = adminNewsAutomationService.getId(article);
    setArticles((prev) =>
      prev.filter((a) => adminNewsAutomationService.getId(a) !== id)
    );
    updateTotal(Math.max(0, total - 1));
  };

  const handleApprove = async () => {
    if (!selected) return;
    if (!confirmApprove) {
      setConfirmApprove(true);
      return;
    }
    const id = adminNewsAutomationService.getId(selected);
    if (!id) return;
    setActionLoading(true);
    try {
      await adminNewsAutomationService.approveArticle(id);
      removeFromList(selected);
      setFeedback(`„${newsService.formatTitle(selected)}“ wurde freigegeben.`);
      closeModal();
    } catch (err) {
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Artikel konnte nicht freigegeben werden.'
        )
      );
      closeModal();
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    if (!showReject) {
      setShowReject(true);
      setConfirmApprove(false);
      return;
    }
    const id = adminNewsAutomationService.getId(selected);
    if (!id) return;
    setActionLoading(true);
    try {
      await adminNewsAutomationService.rejectArticle(id, rejectReason);
      removeFromList(selected);
      setFeedback(`„${newsService.formatTitle(selected)}“ wurde abgelehnt.`);
      closeModal();
    } catch (err) {
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Artikel konnte nicht abgelehnt werden.'
        )
      );
      closeModal();
    } finally {
      setActionLoading(false);
    }
  };

  const renderModalContent = () => {
    if (!selected) return null;
    const rawHtml = selected.contentHtml?.trim() ?? '';
    const rawContent = selected.content?.trim() ?? '';
    return (
      <>
        <ArticleBanner article={selected} className="rounded-xl" />
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-gray-400">
          <Chip
            size="sm"
            startContent={<Bot size={13} />}
            className="bg-[#3fced1]/15 text-[#3fced1] gap-1 px-2"
          >
            KI-generiert
          </Chip>
          <span className="flex items-center gap-1">
            <Clock size={13} />
            {newsService.formatArticleTime(selected) || 'unbekannt'}
          </span>
          {selected.slug && <span className="text-gray-500">/{selected.slug}</span>}
        </div>
        <h2 className="mt-3 text-xl sm:text-2xl font-bold text-white leading-tight">
          {newsService.formatTitle(selected)}
        </h2>
        {newsService.formatDescription(selected) && (
          <p className="mt-2 text-sm text-gray-400">
            {newsService.formatDescription(selected)}
          </p>
        )}
        <div className="mt-5">
          {rawHtml ? (
            <div
              className="prose prose-invert max-w-none text-gray-300 leading-relaxed text-[15px]"
              dangerouslySetInnerHTML={{ __html: rawHtml }}
            />
          ) : rawContent ? (
            <div className="text-gray-300 leading-relaxed text-[15px] whitespace-pre-wrap">
              {rawContent}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Kein Inhalt vorhanden.</p>
          )}
        </div>
        {selected.tags && selected.tags.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {selected.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-full text-xs bg-zinc-800 text-gray-400"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div>
      <FeedbackBanner
        message={feedback}
        type="success"
        onClose={() => setFeedback(null)}
      />
      <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

      {articles.length === 0 ? (
        <Card className="bg-zinc-900 border border-zinc-800">
          <CardBody className="p-10 flex flex-col items-center text-center">
            <Sparkles size={48} className="text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-1">
              Keine Artikel zur Prüfung
            </h3>
            <p className="text-sm text-gray-400">
              Aktuell warten keine KI-generierten Artikel auf ein Review.
            </p>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map((article, index) => {
              const id = adminNewsAutomationService.getId(article) || `idx-${index}`;
              return (
                <Card
                  key={id}
                  isPressable
                  onPress={() => openArticle(article)}
                  className="bg-zinc-900 border border-zinc-800 hover:border-[#3fced1]/60 transition-colors overflow-hidden"
                >
                  <CardBody className="p-0">
                    <div className="relative">
                      <ArticleBanner article={article} />
                      <Chip
                        size="sm"
                        startContent={<Bot size={13} />}
                        className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm text-[#3fced1] gap-1 px-2"
                      >
                        KI-generiert
                      </Chip>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-white line-clamp-2">
                        {newsService.formatTitle(article)}
                      </h3>
                      {newsService.formatDescription(article) && (
                        <p className="mt-1.5 text-xs text-gray-400 line-clamp-2">
                          {newsService.formatDescription(article)}
                        </p>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock size={12} />
                          {newsService.formatArticleTime(article) || 'unbekannt'}
                        </span>
                        {(article.tags || []).slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-full text-[11px] bg-zinc-800 text-gray-400"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>

          {articles.length < total && (
            <div className="mt-6 flex justify-center">
              <Button
                variant="bordered"
                className="border-zinc-700 text-gray-300"
                isLoading={loadingMore}
                onPress={loadMore}
              >
                Mehr laden ({articles.length} / {total})
              </Button>
            </div>
          )}
        </>
      )}

      {/* Artikel-Modal */}
      <Modal
        isOpen={selected !== null}
        onClose={closeModal}
        size="3xl"
        scrollBehavior="inside"
        classNames={{
          base: 'bg-zinc-900 border border-zinc-800',
          closeButton: 'text-gray-400 hover:bg-zinc-800',
        }}
      >
        <ModalContent>
          <ModalHeader className="text-white border-b border-zinc-800 text-base">
            Artikel prüfen
          </ModalHeader>
          <ModalBody className="py-5">{renderModalContent()}</ModalBody>
          <ModalFooter className="border-t border-zinc-800 flex-col items-stretch gap-3">
            {showReject && (
              <Textarea
                label="Grund der Ablehnung (optional)"
                placeholder="z. B. inhaltlich falsch, doppelt, unpassend …"
                value={rejectReason}
                onValueChange={setRejectReason}
                minRows={2}
                classNames={{
                  inputWrapper: 'bg-zinc-800 border border-zinc-700',
                  input: 'text-white',
                  label: 'text-gray-400',
                }}
              />
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="bordered"
                className="border-red-500/50 text-red-400"
                startContent={!actionLoading ? <XCircle size={16} /> : undefined}
                isLoading={actionLoading && showReject}
                isDisabled={actionLoading && !showReject}
                onPress={handleReject}
              >
                {showReject ? 'Ablehnen bestätigen' : 'Ablehnen'}
              </Button>
              <Button
                className="font-semibold"
                style={{ backgroundColor: ACCENT, color: '#000' }}
                startContent={!actionLoading ? <CheckCircle2 size={16} /> : undefined}
                isLoading={actionLoading && !showReject}
                isDisabled={actionLoading && showReject}
                onPress={handleApprove}
              >
                {confirmApprove ? 'Wirklich freigeben?' : 'Freigeben'}
              </Button>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

// ---------- Quellen-Tab ----------

interface SourceFormState {
  name: string;
  type: AutomationSourceType;
  seriesId: string;
  tournamentId: string;
  url: string;
  country: string;
  enabled: boolean;
}

const emptySourceForm: SourceFormState = {
  name: '',
  type: 'DARTCONNECT_TOURNAMENT',
  seriesId: '',
  tournamentId: '',
  url: '',
  country: '',
  enabled: true,
};

const SourcesTab: React.FC = () => {
  const [sources, setSources] = useState<AutomationSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SourceFormState>(emptySourceForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminNewsAutomationService.getSources();
      setSources(res);
    } catch (err) {
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Quellen konnten nicht geladen werden.'
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptySourceForm);
    setFormError(null);
    setConfirmDelete(false);
    setModalOpen(true);
  };

  const openEdit = (source: AutomationSource) => {
    const id = adminNewsAutomationService.getId(source);
    if (!id) return;
    setEditingId(id);
    setForm({
      name: source.name || '',
      type:
        source.type === 'MSS_SERIES' || source.type === 'WEBSITE'
          ? source.type
          : 'DARTCONNECT_TOURNAMENT',
      seriesId:
        source.config?.seriesId !== null && source.config?.seriesId !== undefined
          ? String(source.config.seriesId)
          : '',
      tournamentId: source.config?.tournamentId || '',
      url: source.config?.url || '',
      country: source.config?.country || '',
      enabled: source.enabled !== false,
    });
    setFormError(null);
    setConfirmDelete(false);
    setModalOpen(true);
  };

  const buildPayload = () => {
    const config: {
      tournamentId?: string;
      seriesId?: number;
      url?: string;
      country?: string;
    } = {};
    if (form.type === 'DARTCONNECT_TOURNAMENT' && form.tournamentId.trim()) {
      config.tournamentId = form.tournamentId.trim();
    }
    if (form.type === 'MSS_SERIES' && form.seriesId.trim()) {
      const parsed = parseInt(form.seriesId.trim(), 10);
      if (!isNaN(parsed)) config.seriesId = parsed;
    }
    if (form.type === 'WEBSITE' && form.url.trim()) {
      config.url = form.url.trim();
    }
    if (form.country.trim()) config.country = form.country.trim();
    return {
      name: form.name.trim(),
      type: form.type,
      config,
      enabled: form.enabled,
    };
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('Bitte einen Namen angeben.');
      return;
    }
    if (form.type === 'MSS_SERIES' && form.seriesId.trim()) {
      const parsed = parseInt(form.seriesId.trim(), 10);
      if (isNaN(parsed)) {
        setFormError('Die Serien-ID muss eine Zahl sein.');
        return;
      }
    }
    if (form.type === 'WEBSITE') {
      const url = form.url.trim();
      if (!/^https?:\/\/.+/.test(url)) {
        setFormError('Bitte eine gültige URL angeben (https://…).');
        return;
      }
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editingId) {
        await adminNewsAutomationService.updateSource(editingId, buildPayload());
        setFeedback('Quelle wurde aktualisiert.');
      } else {
        await adminNewsAutomationService.createSource(buildPayload());
        setFeedback('Quelle wurde angelegt.');
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      setFormError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Quelle konnte nicht gespeichert werden.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await adminNewsAutomationService.deleteSource(editingId);
      setFeedback('Quelle wurde gelöscht.');
      setModalOpen(false);
      await load();
    } catch (err) {
      setFormError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Quelle konnte nicht gelöscht werden.'
        )
      );
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleToggleEnabled = async (source: AutomationSource, enabled: boolean) => {
    const id = adminNewsAutomationService.getId(source);
    if (!id) return;
    setTogglingId(id);
    // Optimistisches Update
    setSources((prev) =>
      prev.map((s) =>
        adminNewsAutomationService.getId(s) === id ? { ...s, enabled } : s
      )
    );
    try {
      await adminNewsAutomationService.updateSource(id, { enabled });
    } catch (err) {
      // Zurückrollen
      setSources((prev) =>
        prev.map((s) =>
          adminNewsAutomationService.getId(s) === id ? { ...s, enabled: !enabled } : s
        )
      );
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Status konnte nicht geändert werden.'
        )
      );
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div>
      <FeedbackBanner
        message={feedback}
        type="success"
        onClose={() => setFeedback(null)}
      />
      <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

      <div className="mb-4 flex justify-end">
        <Button
          className="font-semibold"
          style={{ backgroundColor: ACCENT, color: '#000' }}
          startContent={<Plus size={16} />}
          onPress={openCreate}
        >
          Quelle hinzufügen
        </Button>
      </div>

      {sources.length === 0 ? (
        <Card className="bg-zinc-900 border border-zinc-800">
          <CardBody className="p-10 flex flex-col items-center text-center">
            <Globe size={48} className="text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-1">
              Noch keine Quellen
            </h3>
            <p className="text-sm text-gray-400">
              Lege die erste Quelle an, damit die Automation Themen findet.
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sources.map((source, index) => {
            const id = adminNewsAutomationService.getId(source) || `idx-${index}`;
            const isMss = source.type === 'MSS_SERIES';
            return (
              <Card key={id} className="bg-zinc-900 border border-zinc-800">
                <CardBody className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => openEdit(source)}
                      className="flex-1 min-w-0 text-left group"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-white group-hover:text-[#3fced1] transition-colors truncate">
                          {source.name || 'Unbenannte Quelle'}
                        </span>
                        <Pencil
                          size={13}
                          className="text-gray-500 shrink-0 group-hover:text-[#3fced1] transition-colors"
                        />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Chip
                          size="sm"
                          startContent={isMss ? <Trophy size={12} /> : <Globe size={12} />}
                          className={`gap-1 px-2 ${
                            isMss
                              ? 'bg-purple-500/15 text-purple-400'
                              : 'bg-[#3fced1]/15 text-[#3fced1]'
                          }`}
                        >
                          {sourceTypeLabel(source.type)}
                        </Chip>
                        {source.config?.seriesId !== null &&
                          source.config?.seriesId !== undefined && (
                            <span className="text-xs text-gray-400">
                              Serien-ID: {source.config.seriesId}
                            </span>
                          )}
                        {source.config?.tournamentId && (
                          <span className="text-xs text-gray-400">
                            Turnier-ID: {source.config.tournamentId}
                          </span>
                        )}
                        {source.config?.url && (
                          <span className="text-xs text-gray-400 truncate max-w-[200px]">
                            {source.config.url}
                          </span>
                        )}
                        {source.config?.country && (
                          <Chip size="sm" className="bg-zinc-800 text-gray-300 px-2">
                            {source.config.country}
                          </Chip>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                        <Clock size={12} />
                        {source.lastCheckedAt
                          ? `Zuletzt geprüft ${newsService.formatTimeAgo(source.lastCheckedAt)}`
                          : 'Noch nie geprüft'}
                      </div>
                      {source.lastError && (
                        <p className="mt-2 text-xs text-red-400 flex items-start gap-1">
                          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                          <span className="break-words">{source.lastError}</span>
                        </p>
                      )}
                    </button>
                    <Switch
                      size="sm"
                      isSelected={source.enabled !== false}
                      isDisabled={togglingId === adminNewsAutomationService.getId(source)}
                      onValueChange={(value) => handleToggleEnabled(source, value)}
                      aria-label={`Quelle ${source.name || ''} aktivieren`}
                      classNames={{
                        wrapper: 'group-data-[selected=true]:bg-[#3fced1]',
                      }}
                    />
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      {/* Quelle-Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        size="lg"
        scrollBehavior="inside"
        classNames={{
          base: 'bg-zinc-900 border border-zinc-800',
          closeButton: 'text-gray-400 hover:bg-zinc-800',
        }}
      >
        <ModalContent>
          <ModalHeader className="text-white border-b border-zinc-800 text-base">
            {editingId ? 'Quelle bearbeiten' : 'Quelle hinzufügen'}
          </ModalHeader>
          <ModalBody className="py-5 gap-4">
            {formError && (
              <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {formError}
              </div>
            )}
            <Input
              label="Name"
              isRequired
              placeholder="z. B. PDC Europe Tour"
              value={form.name}
              onValueChange={(value) => setForm((f) => ({ ...f, name: value }))}
              classNames={{
                inputWrapper: 'bg-zinc-800 border border-zinc-700',
                input: 'text-white',
                label: 'text-gray-400',
              }}
            />
            <Select
              label="Typ"
              selectedKeys={[form.type]}
              disallowEmptySelection
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0] as AutomationSourceType | undefined;
                if (key) setForm((f) => ({ ...f, type: key }));
              }}
              classNames={{
                trigger: 'bg-zinc-800 border border-zinc-700',
                value: 'text-white',
                label: 'text-gray-400',
                popoverContent: 'bg-zinc-900 border border-zinc-800',
              }}
            >
              <SelectItem key="DARTCONNECT_TOURNAMENT">
                DartConnect-Turnier
              </SelectItem>
              <SelectItem key="MSS_SERIES">MSS-Serie</SelectItem>
              <SelectItem key="WEBSITE">Website / News-Seite</SelectItem>
            </Select>
            {form.type === 'WEBSITE' ? (
              <Input
                label="Website-URL"
                placeholder="z. B. https://dartsnews.de/"
                value={form.url}
                onValueChange={(value) => setForm((f) => ({ ...f, url: value }))}
                description="RSS-Feed wird automatisch erkannt; sonst wird die Seite selbst ausgelesen."
                classNames={{
                  inputWrapper: 'bg-zinc-800 border border-zinc-700',
                  input: 'text-white',
                  label: 'text-gray-400',
                  description: 'text-gray-500',
                }}
              />
            ) : form.type === 'MSS_SERIES' ? (
              <Input
                label="Serien-ID"
                type="number"
                placeholder="z. B. 42"
                value={form.seriesId}
                onValueChange={(value) => setForm((f) => ({ ...f, seriesId: value }))}
                classNames={{
                  inputWrapper: 'bg-zinc-800 border border-zinc-700',
                  input: 'text-white',
                  label: 'text-gray-400',
                }}
              />
            ) : (
              <Input
                label="Turnier-ID"
                placeholder="z. B. abc123"
                value={form.tournamentId}
                onValueChange={(value) =>
                  setForm((f) => ({ ...f, tournamentId: value }))
                }
                classNames={{
                  inputWrapper: 'bg-zinc-800 border border-zinc-700',
                  input: 'text-white',
                  label: 'text-gray-400',
                }}
              />
            )}
            <Input
              label="Land (optional)"
              placeholder="z. B. DE"
              value={form.country}
              onValueChange={(value) => setForm((f) => ({ ...f, country: value }))}
              classNames={{
                inputWrapper: 'bg-zinc-800 border border-zinc-700',
                input: 'text-white',
                label: 'text-gray-400',
              }}
            />
            <Switch
              isSelected={form.enabled}
              onValueChange={(value) => setForm((f) => ({ ...f, enabled: value }))}
              classNames={{ label: 'text-gray-300 text-sm' }}
            >
              Quelle aktiv
            </Switch>
          </ModalBody>
          <ModalFooter className="border-t border-zinc-800">
            {editingId && (
              <Button
                variant="bordered"
                className="border-red-500/50 text-red-400 mr-auto"
                startContent={!deleting ? <Trash2 size={16} /> : undefined}
                isLoading={deleting}
                onPress={handleDelete}
              >
                {confirmDelete ? 'Wirklich löschen?' : 'Löschen'}
              </Button>
            )}
            <Button
              variant="light"
              className="text-gray-400"
              onPress={() => setModalOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              className="font-semibold"
              style={{ backgroundColor: ACCENT, color: '#000' }}
              isLoading={saving}
              onPress={handleSave}
            >
              Speichern
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

// ---------- Einstellungen-Tab ----------

interface SettingsTabProps {
  onRunStarted: () => void;
}

const SettingsTab: React.FC<SettingsTabProps> = ({ onRunStarted }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRunAt, setLastRunAt] = useState<number | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState('60');
  const [autoPublish, setAutoPublish] = useState(false);
  const [pdcAutoDiscovery, setPdcAutoDiscovery] = useState(true);
  const [maxArticlesPerRun, setMaxArticlesPerRun] = useState('3');
  const [claudeModel, setClaudeModel] = useState('');

  useEffect(() => {
    let cancelled = false;
    adminNewsAutomationService
      .getSettings()
      .then((settings) => {
        if (cancelled) return;
        setEnabled(settings.enabled === true);
        setIntervalMinutes(String(settings.intervalMinutes ?? 60));
        setAutoPublish(settings.autoPublish === true);
        setPdcAutoDiscovery(settings.pdcAutoDiscovery !== false);
        setMaxArticlesPerRun(String(settings.maxArticlesPerRun ?? 3));
        setClaudeModel(settings.claudeModel || '');
        setLastRunAt(
          typeof settings.lastRunAt === 'number' ? settings.lastRunAt : null
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            adminNewsAutomationService.getErrorMessage(
              err,
              'Einstellungen konnten nicht geladen werden.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    const interval = parseInt(intervalMinutes, 10);
    if (isNaN(interval) || interval < 10 || interval > 120) {
      setError('Das Intervall muss zwischen 10 und 120 Minuten liegen.');
      return;
    }
    const maxArticles = parseInt(maxArticlesPerRun, 10);
    if (isNaN(maxArticles) || maxArticles < 1 || maxArticles > 10) {
      setError('Max. Artikel pro Lauf muss zwischen 1 und 10 liegen.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const settings = await adminNewsAutomationService.updateSettings({
        enabled,
        intervalMinutes: interval,
        autoPublish,
        pdcAutoDiscovery,
        maxArticlesPerRun: maxArticles,
        claudeModel: claudeModel.trim(),
      });
      setLastRunAt(
        typeof settings.lastRunAt === 'number' ? settings.lastRunAt : lastRunAt
      );
      setFeedback('Einstellungen wurden gespeichert.');
    } catch (err) {
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Einstellungen konnten nicht gespeichert werden.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    setError(null);
    try {
      await adminNewsAutomationService.triggerRun();
      onRunStarted();
    } catch (err) {
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Lauf konnte nicht gestartet werden.'
        )
      );
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <FeedbackBanner
        message={feedback}
        type="success"
        onClose={() => setFeedback(null)}
      />
      <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

      <Card className="bg-zinc-900 border border-zinc-800">
        <CardBody className="p-5 sm:p-6 gap-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-white">Automation aktiv</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Regelmäßige Läufe im eingestellten Intervall ausführen.
              </p>
            </div>
            <Switch
              isSelected={enabled}
              onValueChange={setEnabled}
              aria-label="Automation aktiv"
            />
          </div>

          <Input
            label="Intervall (Minuten, 10–120)"
            type="number"
            min={10}
            max={120}
            value={intervalMinutes}
            onValueChange={setIntervalMinutes}
            classNames={{
              inputWrapper: 'bg-zinc-800 border border-zinc-700',
              input: 'text-white',
              label: 'text-gray-400',
            }}
          />

          <div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-white">Auto-Publish</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Neue KI-Artikel ohne Review direkt veröffentlichen.
                </p>
              </div>
              <Switch
                isSelected={autoPublish}
                onValueChange={setAutoPublish}
                aria-label="Auto-Publish"
              />
            </div>
            {autoPublish && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-orange-500/40 bg-orange-500/10 px-4 py-3 text-sm text-orange-400">
                <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                <span>
                  Achtung: Artikel gehen ohne Review live! Alle generierten Artikel
                  werden sofort veröffentlicht.
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-white">PDC-Auto-Discovery</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Bei jedem Lauf automatisch laufende PDC-Turniere über die
                DartConnect-API finden – ohne manuell angelegte Quelle.
              </p>
            </div>
            <Switch
              isSelected={pdcAutoDiscovery}
              onValueChange={setPdcAutoDiscovery}
              aria-label="PDC-Auto-Discovery"
            />
          </div>

          <Input
            label="Max. Artikel pro Lauf (1–10)"
            type="number"
            min={1}
            max={10}
            value={maxArticlesPerRun}
            onValueChange={setMaxArticlesPerRun}
            classNames={{
              inputWrapper: 'bg-zinc-800 border border-zinc-700',
              input: 'text-white',
              label: 'text-gray-400',
            }}
          />

          <Input
            label="Claude-Modell (optional)"
            placeholder="z. B. claude-sonnet-4-5"
            value={claudeModel}
            onValueChange={setClaudeModel}
            classNames={{
              inputWrapper: 'bg-zinc-800 border border-zinc-700',
              input: 'text-white',
              label: 'text-gray-400',
            }}
          />

          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Clock size={13} />
            {lastRunAt
              ? `Letzter Lauf: ${formatDateTime(lastRunAt)} (${newsService.formatTimeAgo(lastRunAt)})`
              : 'Noch kein Lauf ausgeführt.'}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Button
              className="font-semibold"
              style={{ backgroundColor: ACCENT, color: '#000' }}
              startContent={!saving ? <Save size={16} /> : undefined}
              isLoading={saving}
              onPress={handleSave}
            >
              Speichern
            </Button>
            <Button
              variant="bordered"
              className="border-[#3fced1]/60 text-[#3fced1] font-semibold"
              startContent={!running ? <Play size={16} /> : undefined}
              isLoading={running}
              onPress={handleRunNow}
            >
              Jetzt ausführen
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

// ---------- Läufe-Tab ----------

interface RunsTabProps {
  refreshToken: number;
}

const RunStatusChip: React.FC<{ status?: string | null }> = ({ status }) => {
  if (status === 'running') {
    return (
      <Chip
        size="sm"
        className="bg-[#3fced1]/15 text-[#3fced1] animate-pulse font-semibold px-2"
      >
        Läuft …
      </Chip>
    );
  }
  if (status === 'success') {
    return (
      <Chip size="sm" className="bg-green-500/15 text-green-400 font-semibold px-2">
        Erfolgreich
      </Chip>
    );
  }
  if (status === 'error') {
    return (
      <Chip size="sm" className="bg-red-500/15 text-red-400 font-semibold px-2">
        Fehler
      </Chip>
    );
  }
  return (
    <Chip size="sm" className="bg-zinc-800 text-gray-400 px-2">
      {status || 'Unbekannt'}
    </Chip>
  );
};

const RunsTab: React.FC<RunsTabProps> = ({ refreshToken }) => {
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());
  const runsRef = useRef<AutomationRun[]>([]);
  runsRef.current = runs;

  const load = useCallback(async (limit: number, showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await adminNewsAutomationService.getRuns({ limit, offset: 0 });
      setRuns(res.runs);
      setTotal(res.total);
      setError(null);
    } catch (err) {
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Läufe konnten nicht geladen werden.'
        )
      );
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  // Initial + bei externem Refresh (z. B. nach „Jetzt ausführen“)
  useEffect(() => {
    load(RUNS_PAGE_SIZE, true);
  }, [load, refreshToken]);

  // Auto-Polling alle 10s, solange ein Run läuft (Cleanup bei Unmount!)
  const hasRunningRun = runs.some((run) => run.status === 'running');
  useEffect(() => {
    if (!hasRunningRun) return;
    const timer = setInterval(() => {
      const currentCount = Math.max(runsRef.current.length, RUNS_PAGE_SIZE);
      load(currentCount, false);
    }, 10000);
    return () => clearInterval(timer);
  }, [hasRunningRun, load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await adminNewsAutomationService.getRuns({
        limit: RUNS_PAGE_SIZE,
        offset: runs.length,
      });
      setRuns((prev) => {
        const existingIds = new Set(
          prev.map((r) => adminNewsAutomationService.getId(r)).filter(Boolean)
        );
        const fresh = res.runs.filter(
          (r) => !existingIds.has(adminNewsAutomationService.getId(r))
        );
        return [...prev, ...fresh];
      });
      setTotal(res.total);
    } catch (err) {
      setError(
        adminNewsAutomationService.getErrorMessage(
          err,
          'Weitere Läufe konnten nicht geladen werden.'
        )
      );
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleErrors = (id: string) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div>
      <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

      {runs.length === 0 ? (
        <Card className="bg-zinc-900 border border-zinc-800">
          <CardBody className="p-10 flex flex-col items-center text-center">
            <History size={48} className="text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-1">
              Noch keine Läufe
            </h3>
            <p className="text-sm text-gray-400">
              Starte einen Lauf über den Einstellungen-Tab („Jetzt ausführen“).
            </p>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {runs.map((run, index) => {
              const id = adminNewsAutomationService.getId(run) || `idx-${index}`;
              const duration = formatDuration(run.startedAt, run.finishedAt);
              const errors = run.errors || [];
              const topics = run.topics || [];
              const errorsExpanded = expandedErrors.has(id);
              return (
                <Card key={id} className="bg-zinc-900 border border-zinc-800">
                  <CardBody className="p-4">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <RunStatusChip status={run.status} />
                      <span className="text-sm text-gray-300">
                        {formatDateTime(run.startedAt) || 'Startzeit unbekannt'}
                      </span>
                      {duration && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock size={12} />
                          {duration}
                        </span>
                      )}
                      {run.triggeredBy && (
                        <span className="text-xs text-gray-500">
                          Ausgelöst von: {run.triggeredBy}
                        </span>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">
                        {run.articlesCreated ?? 0}{' '}
                        {(run.articlesCreated ?? 0) === 1 ? 'Artikel' : 'Artikel'}{' '}
                        erstellt
                      </span>
                    </div>

                    {topics.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {topics.map((topic, topicIndex) => (
                          <span
                            key={`${topic}-${topicIndex}`}
                            className="px-2 py-0.5 rounded-full text-[11px] bg-zinc-800 text-gray-400"
                          >
                            {topic}
                          </span>
                        ))}
                      </div>
                    )}

                    {errors.length > 0 && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => toggleErrors(id)}
                          className="flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-300"
                        >
                          <AlertTriangle size={13} />
                          {errors.length}{' '}
                          {errors.length === 1 ? 'Fehler' : 'Fehler'}
                          {errorsExpanded ? (
                            <ChevronUp size={13} />
                          ) : (
                            <ChevronDown size={13} />
                          )}
                        </button>
                        {errorsExpanded && (
                          <ul className="mt-2 flex flex-col gap-1.5 rounded-xl border border-red-500/30 bg-red-500/5 p-3">
                            {errors.map((message, errorIndex) => (
                              <li
                                key={errorIndex}
                                className="text-xs text-red-400 break-words"
                              >
                                • {message}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </div>

          {runs.length < total && (
            <div className="mt-6 flex justify-center">
              <Button
                variant="bordered"
                className="border-zinc-700 text-gray-300"
                isLoading={loadingMore}
                onPress={loadMore}
              >
                Mehr laden ({runs.length} / {total})
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Hauptseite ----------

const TabLabel: React.FC<{
  icon: React.ReactNode;
  label: string;
  count?: number | null;
}> = ({ icon, label, count }) => (
  <div className="flex items-center gap-2">
    {icon}
    <span>{label}</span>
    {count !== null && count !== undefined && count > 0 && (
      <Chip size="sm" className="bg-[#3fced1] text-black font-semibold h-5 px-1.5">
        {count}
      </Chip>
    )}
  </div>
);

const AdminNewsAutomationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('review');
  const [pendingTotal, setPendingTotal] = useState<number | null>(null);
  const [runsRefreshToken, setRunsRefreshToken] = useState(0);

  const handleRunStarted = () => {
    setRunsRefreshToken((token) => token + 1);
    setActiveTab('runs');
  };

  return (
    <>
      <Helmet>
        <title>News-Automation | Admin | DartCloud</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full">
        {/* Hero */}
        <section className="border-b border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-8 sm:py-10">
            <Button
              as={RouterLink as any}
              to="/admin"
              variant="light"
              startContent={<ArrowLeft size={16} />}
              className="text-white px-0 mb-4"
            >
              Zurück zum Admin-Bereich
            </Button>
            <div className="flex items-center gap-3 mb-2">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
              >
                <Sparkles size={24} />
              </span>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight">
                News-Automation
              </h1>
            </div>
            <p className="text-sm sm:text-base text-gray-400 max-w-2xl">
              KI-generierte Artikel prüfen, Quellen verwalten und Läufe überwachen.
            </p>
          </div>
        </section>

        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-6">
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={(key) => setActiveTab(key as TabKey)}
            variant="solid"
            classNames={{
              tabList: 'bg-zinc-900/70 border border-zinc-800 p-1 rounded-xl flex-wrap',
              cursor: 'bg-zinc-800',
              tab: 'text-zinc-300 data-[selected=true]:text-white',
              tabContent: 'group-data-[selected=true]:text-white',
            }}
          >
            <Tab
              key="review"
              title={
                <TabLabel
                  icon={<Newspaper className="w-4 h-4" />}
                  label="Review"
                  count={pendingTotal}
                />
              }
            />
            <Tab
              key="sources"
              title={<TabLabel icon={<Globe className="w-4 h-4" />} label="Quellen" />}
            />
            <Tab
              key="settings"
              title={
                <TabLabel
                  icon={<Settings className="w-4 h-4" />}
                  label="Einstellungen"
                />
              }
            />
            <Tab
              key="runs"
              title={<TabLabel icon={<History className="w-4 h-4" />} label="Läufe" />}
            />
          </Tabs>

          <div className="mt-6">
            {activeTab === 'review' && (
              <ReviewTab onPendingTotalChange={setPendingTotal} />
            )}
            {activeTab === 'sources' && <SourcesTab />}
            {activeTab === 'settings' && (
              <SettingsTab onRunStarted={handleRunStarted} />
            )}
            {activeTab === 'runs' && <RunsTab refreshToken={runsRefreshToken} />}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminNewsAutomationPage;
